import {
  Bookmark,
  BookmarkCheck,
  ChevronLeft,
  ExternalLink,
  Image,
  LayoutGrid,
  List,
  ListChecks,
  UserRound,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useApp } from '../components/app-context';
import { Button, ButtonLink } from '../components/button';
import { LoadingWall, RequestErrorWall } from '../components/content-wall';
import { EpisodeList, hasEpisodeAired } from '../components/episode-list';
import { MediaSourceDialog } from '../components/media-source-dialog';
import { Select } from '../components/select';
import { ToggleGroup } from '../components/toggle-group';
import { getLocale, t } from '../i18n';
import { useStore } from '../hooks/use-store';
import { $currentUser } from '../store/app.store';
import type {
  BgmCurrentUser,
  Episode,
  EpisodeCollectionType,
  EpisodePage,
  RelatedCharacter,
  RelatedPerson,
  RelatedSubject,
  SubjectBroadcast,
  SubjectDetail,
  UserEpisodeCollectionPage,
  UserSubjectCollection,
} from '../types/bgm';
import type { WatchLaterPage } from '../types/page';
import { safeUrl } from '../utils/url';

const primaryStaffRoles = [
  '动画制作',
  '制作',
  '製作',
  '制作协力',
  '原作',
  '原作者',
  '作者',
  '原案',
  '出版社',
  '连载杂志',
  '导演',
  '监督',
  '总导演',
  '副导演',
  '系列构成',
  '脚本',
  '编剧',
  '人物设定',
  '角色设计',
  '总作画监督',
  '音乐',
  '音乐制作',
  '音响监督',
  '美术监督',
  '摄影监督',
  '剪辑',
  '制片人',
  '企画',
  '出品',
  '发行',
] as const;
const staffPriority = new Map<string, number>(primaryStaffRoles.map((role, index) => [role, index]));

const weeklyBroadcast = (broadcastAt: string, locale: ReturnType<typeof getLocale>): string => {
  const date = new Date(broadcastAt);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  const weekday = new Intl.DateTimeFormat(locale, { weekday: locale === 'zh-CN' ? 'short' : 'long' }).format(date);
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
  return locale === 'zh-CN' ? `每${weekday} ${time}` : `Every ${weekday} ${time}`;
};

interface SubjectData {
  subject: SubjectDetail;
  episodes: EpisodePage;
  characters: RelatedCharacter[];
  persons: RelatedPerson[];
  related: RelatedSubject[];
  broadcast?: SubjectBroadcast;
  collection?: UserSubjectCollection;
  episodeStatuses: Record<number, EpisodeCollectionType>;
}

export const SubjectPage = ({ subjectId }: { subjectId: number }) => {
  const { api, bgm, bgmAuth, navigate, showToast } = useApp();
  const currentUser = useStore($currentUser);
  const hasBangumiToken = Boolean(currentUser?.bgmToken);
  const username = useRef<string | undefined>(undefined);
  const selectAllEpisodesRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<SubjectData>();
  const [error, setError] = useState('');
  const [retryRevision, setRetryRevision] = useState(0);
  const [charactersExpanded, setCharactersExpanded] = useState(false);
  const [staffExpanded, setStaffExpanded] = useState(false);
  const [selectedEpisodeIds, setSelectedEpisodeIds] = useState<Set<number>>(() => new Set());
  const [episodeMarkType, setEpisodeMarkType] = useState<EpisodeCollectionType>(2);
  const [updatingEpisodes, setUpdatingEpisodes] = useState(false);
  const [episodeLayout, setEpisodeLayout] = useState<'list' | 'grid'>('list');
  const [episodeSelectionMode, setEpisodeSelectionMode] = useState(false);
  const [mediaEpisode, setMediaEpisode] = useState<Episode>();
  const [watchLaterEpisodeIds, setWatchLaterEpisodeIds] = useState<Set<number>>(() => new Set());
  const [watchLaterUpdatingEpisodeIds, setWatchLaterUpdatingEpisodeIds] = useState<Set<number>>(() => new Set());
  const allEpisodesSelected =
    Boolean(data?.episodes.data.length) &&
    (data?.episodes.data.every((episode) => selectedEpisodeIds.has(episode.id)) ?? false);
  const someEpisodesSelected = selectedEpisodeIds.size > 0 && !allEpisodesSelected;

  useEffect(() => {
    if (selectAllEpisodesRef.current) {
      selectAllEpisodesRef.current.indeterminate = someEpisodesSelected;
    }
  }, [someEpisodesSelected]);

  useEffect(() => {
    let active = true;
    setData(undefined);
    setError('');
    setCharactersExpanded(false);
    setStaffExpanded(false);
    setSelectedEpisodeIds(new Set());
    setEpisodeLayout('list');
    setEpisodeSelectionMode(false);
    setMediaEpisode(undefined);
    setWatchLaterEpisodeIds(new Set());
    setWatchLaterUpdatingEpisodeIds(new Set());
    const collection = async (): Promise<UserSubjectCollection | undefined> => {
      if (!hasBangumiToken) {
        return undefined;
      }
      try {
        if (!username.current) {
          username.current = (await bgmAuth<BgmCurrentUser>('/v0/me')).username;
        }
        return await bgmAuth<UserSubjectCollection>(
          `/v0/users/${encodeURIComponent(username.current)}/collections/${subjectId}`,
        );
      } catch {
        return undefined;
      }
    };
    const episodeCollections = async (): Promise<UserEpisodeCollectionPage | undefined> => {
      if (!hasBangumiToken) {
        return undefined;
      }
      try {
        return await bgmAuth<UserEpisodeCollectionPage>(
          `/v0/users/-/collections/${subjectId}/episodes?limit=1000&episode_type=0`,
        );
      } catch {
        return undefined;
      }
    };
    const broadcast = async (): Promise<SubjectBroadcast | undefined> => {
      try {
        return (
          await api<SubjectBroadcast[]>('/api/schedule', {
            method: 'POST',
            body: JSON.stringify({ subjectIds: [subjectId] }),
          })
        )[0];
      } catch {
        return undefined;
      }
    };
    const watchLater = async (): Promise<WatchLaterPage> => {
      try {
        return await api<WatchLaterPage>(`/api/watch-later?subjectId=${subjectId}&limit=1000`);
      } catch {
        return { data: [], total: 0 };
      }
    };
    void Promise.all([
      bgm<SubjectDetail>(`/v0/subjects/${subjectId}`),
      bgm<EpisodePage>(`/v0/episodes?subject_id=${subjectId}&type=0&limit=200`),
      bgm<RelatedCharacter[]>(`/v0/subjects/${subjectId}/characters`),
      bgm<RelatedPerson[]>(`/v0/subjects/${subjectId}/persons`),
      bgm<RelatedSubject[]>(`/v0/subjects/${subjectId}/subjects`),
      collection(),
      episodeCollections(),
      broadcast(),
      watchLater(),
    ])
      .then(
        ([
          subject,
          episodes,
          characters,
          persons,
          related,
          currentCollection,
          currentEpisodeCollections,
          subjectBroadcast,
          watchLaterPage,
        ]) => {
          if (active) {
            setData({
              subject,
              episodes,
              characters,
              persons,
              related,
              broadcast: subjectBroadcast,
              collection: currentCollection,
              episodeStatuses: Object.fromEntries(
                (currentEpisodeCollections?.data ?? []).map((item) => [item.episode.id, item.type]),
              ),
            });
            setWatchLaterEpisodeIds(new Set(watchLaterPage.data.map((item) => item.bangumiEpisodeId)));
          }
        },
      )
      .catch((reason: unknown) => {
        if (active) {
          setError(reason instanceof Error ? reason.message : String(reason));
        }
      });
    return () => {
      active = false;
    };
  }, [api, bgm, bgmAuth, hasBangumiToken, retryRevision, subjectId]);

  const staff = useMemo(
    () =>
      (data?.persons ?? [])
        .filter((person) => staffPriority.has(person.relation.trim()))
        .sort(
          (left, right) =>
            (staffPriority.get(left.relation.trim()) ?? primaryStaffRoles.length) -
            (staffPriority.get(right.relation.trim()) ?? primaryStaffRoles.length),
        ),
    [data?.persons],
  );
  const relatedAnime = useMemo(() => (data?.related ?? []).filter((item) => item.type === 2).slice(0, 24), [data]);
  if (error) {
    return <RequestErrorWall onRetry={() => setRetryRevision((current) => current + 1)} />;
  }
  if (!data) {
    return <LoadingWall />;
  }

  const { subject, episodes, characters, collection } = data;
  const image = safeUrl(subject.images?.large ?? subject.images?.common);
  const score = subject.rating?.score ?? subject.score ?? 0;
  const totalEpisodeCount = subject.total_episodes || subject.eps || 0;
  const airedEpisodeCount = episodes.data.filter((episode) => hasEpisodeAired(episode.airdate)).length;
  const hasEnded = totalEpisodeCount > 0 && airedEpisodeCount >= totalEpisodeCount;
  const collectionLabels: Record<number, string> = {
    0: t('collectionNone'),
    1: t('collectionWish'),
    2: t('collectionDone'),
    3: t('collectionDoing'),
    4: t('collectionOnHold'),
    5: t('collectionDropped'),
  };
  const episodeStatusLabels: Record<EpisodeCollectionType, string> = {
    0: t('episodeUnwatched'),
    1: t('episodeWish'),
    2: t('episodeWatched'),
    3: t('episodeDropped'),
  };
  const toggleEpisode = (episodeId: number, selected: boolean): void => {
    setSelectedEpisodeIds((current) => {
      const next = new Set(current);
      if (selected) {
        next.add(episodeId);
      } else {
        next.delete(episodeId);
      }
      return next;
    });
  };
  const toggleAllEpisodes = (): void => {
    setSelectedEpisodeIds(allEpisodesSelected ? new Set() : new Set(episodes.data.map((episode) => episode.id)));
  };
  const toggleEpisodeSelectionMode = (): void => {
    if (episodeSelectionMode) {
      setSelectedEpisodeIds(new Set());
    }
    setEpisodeSelectionMode(!episodeSelectionMode);
  };
  const markSelectedEpisodes = async (type: EpisodeCollectionType): Promise<void> => {
    const episodeIds = [...selectedEpisodeIds];
    if (episodeIds.length === 0 || updatingEpisodes) {
      return;
    }
    setUpdatingEpisodes(true);
    try {
      await bgmAuth(`/v0/users/-/collections/${subject.id}/episodes`, {
        method: 'PATCH',
        body: JSON.stringify({ episode_id: episodeIds, type }),
      });
      setData((current) => {
        if (!current) {
          return current;
        }
        const episodeStatuses = { ...current.episodeStatuses };
        for (const episodeId of episodeIds) {
          episodeStatuses[episodeId] = type;
        }
        return { ...current, episodeStatuses };
      });
      setSelectedEpisodeIds(new Set());
      showToast(t('episodeStatusUpdated'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setUpdatingEpisodes(false);
    }
  };
  const updateCollection = async (type: number): Promise<void> => {
    if (type === 0) {
      return;
    }
    try {
      await bgmAuth(`/v0/users/-/collections/${subject.id}`, {
        method: 'POST',
        body: JSON.stringify({ type }),
      });
      setData((current) =>
        current
          ? {
              ...current,
              collection: { type: type as 1 | 2 | 3 | 4 | 5 },
            }
          : current,
      );
      showToast(t('collectionUpdated'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    }
  };
  const closeMediaSearch = (): void => {
    setMediaEpisode(undefined);
  };
  const openMediaSearch = (episode: Episode): void => {
    setMediaEpisode(episode);
  };
  const toggleWatchLater = async (episode: Episode): Promise<void> => {
    if (watchLaterUpdatingEpisodeIds.has(episode.id)) {
      return;
    }
    const saved = watchLaterEpisodeIds.has(episode.id);
    setWatchLaterEpisodeIds((current) => {
      const next = new Set(current);
      if (saved) {
        next.delete(episode.id);
      } else {
        next.add(episode.id);
      }
      return next;
    });
    setWatchLaterUpdatingEpisodeIds((current) => new Set(current).add(episode.id));
    try {
      if (saved) {
        await api(`/api/watch-later/${episode.id}`, { method: 'DELETE' });
      } else {
        await api('/api/watch-later', {
          method: 'PUT',
          body: JSON.stringify({ bangumiSubjectId: subject.id, bangumiEpisodeId: episode.id }),
        });
      }
    } catch (reason) {
      setWatchLaterEpisodeIds((current) => {
        const next = new Set(current);
        if (saved) {
          next.add(episode.id);
        } else {
          next.delete(episode.id);
        }
        return next;
      });
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setWatchLaterUpdatingEpisodeIds((current) => {
        const next = new Set(current);
        next.delete(episode.id);
        return next;
      });
    }
  };

  return (
    <section className="subject-content mx-auto max-w-280">
      <Button
        variant="plain"
        className="mb-5 inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-xs font-bold text-(--muted) transition-colors duration-180 hover:text-(--input-accent) focus-visible:text-(--input-accent) focus-visible:outline-0"
        onClick={() => (history.length > 1 ? history.back() : navigate('/'))}
      >
        <ChevronLeft className="size-4" />
        <span>{getLocale() === 'zh-CN' ? '返回' : 'Back'}</span>
      </Button>
      <div className="subject-hero">
        <div className="subject-cover">{image ? <img src={image} alt="" /> : <Image />}</div>
        <div className="subject-main">
          <div className="subject-tags">
            {subject.meta_tags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
          <h1>{subject.name_cn || subject.name}</h1>
          {subject.name_cn && <p className="original-name">{subject.name}</p>}
          <div className="subject-rating">
            <strong>★ {score.toFixed(1)}</strong>
          </div>
          <div className="subject-facts mt-6.25 flex flex-wrap gap-7">
            <span>
              {t('airDate')}
              <strong>{subject.date ?? '-'}</strong>
            </span>
            {!hasEnded ? (
              <span>
                {t('airTime')}
                <strong>{data.broadcast ? weeklyBroadcast(data.broadcast.broadcastAt, getLocale()) : '-'}</strong>
              </span>
            ) : null}
            <span>
              {t('platform')}
              <strong>{subject.platform || '-'}</strong>
            </span>
            <span>
              {t('episodeCount')}
              <strong>{subject.eps || subject.total_episodes || '-'}</strong>
            </span>
          </div>
          <div className="subject-actions mt-7.5 flex items-end gap-3">
            {hasBangumiToken ? (
              <label className="grid gap-2">
                <span className="m-0 block text-[11px] font-black text-(--muted)">{t('collectionStatus')}</span>
                <Select
                  value={collection?.type ?? 0}
                  ariaLabel={t('collectionStatus')}
                  onChange={(value) => void updateCollection(Number(value))}
                  options={Object.entries(collectionLabels).map(([value, label]) => ({
                    label,
                    value,
                    disabled: value === '0',
                  }))}
                />
              </label>
            ) : null}
            <ButtonLink href={`https://bgm.tv/subject/${subject.id}`} target="_blank" rel="noreferrer">
              <ExternalLink />
              {t('openBangumi')}
            </ButtonLink>
          </div>
        </div>
      </div>
      <section className="detail-section">
        <h2>{t('summary')}</h2>
        <p className="subject-summary">{subject.summary || '-'}</p>
        <div className="tag-cloud flex flex-wrap gap-1.5">
          {subject.tags.slice(0, 20).map((tag) => (
            <span key={tag.name}>
              {tag.name} <small>{tag.count}</small>
            </span>
          ))}
        </div>
      </section>
      {episodes.data.length > 0 && (
        <section className="detail-section">
          <h2>
            {t('episodes')}
            <small>{episodes.total}</small>
            <div className="episode-heading-actions">
              <Button compact onClick={toggleEpisodeSelectionMode}>
                {episodeSelectionMode ? <X className="size-4!" /> : <ListChecks className="size-4!" />}
                {t(episodeSelectionMode ? 'exitSelection' : 'multiSelect')}
              </Button>
              <ToggleGroup
                ariaLabel={t('episodeLayout')}
                value={episodeLayout}
                onChange={(value) => setEpisodeLayout(value as 'list' | 'grid')}
                items={[
                  { value: 'list', label: t('episodeListView'), icon: <List className="size-3.5!" /> },
                  { value: 'grid', label: t('episodeGridView'), icon: <LayoutGrid className="size-3.5!" /> },
                ]}
              />
            </div>
          </h2>
          {episodeSelectionMode && (
            <div className="episode-toolbar">
              <label className="episode-select-all">
                <input
                  ref={selectAllEpisodesRef}
                  className="episode-checkbox"
                  type="checkbox"
                  checked={allEpisodesSelected}
                  aria-label={t('selectAllEpisodes')}
                  onChange={toggleAllEpisodes}
                />
                <span>
                  {t('selectedEpisodes')} {selectedEpisodeIds.size}/{episodes.data.length}
                </span>
              </label>
              <div className="episode-mark-actions">
                <span>{t('markAs')}</span>
                <Select
                  value={episodeMarkType}
                  ariaLabel={t('markEpisodeStatus')}
                  disabled={selectedEpisodeIds.size === 0 || updatingEpisodes}
                  onChange={(value) => {
                    const type = Number(value) as EpisodeCollectionType;
                    setEpisodeMarkType(type);
                    void markSelectedEpisodes(type);
                  }}
                  options={Object.entries(episodeStatusLabels).map(([value, label]) => ({ value, label }))}
                />
              </div>
            </div>
          )}
          {episodeLayout === 'grid' ? (
            <div className={`episode-grid compact ${episodeSelectionMode ? 'selecting' : ''}`.trim()}>
              {episodes.data.map((episode) => {
                const selected = selectedEpisodeIds.has(episode.id);
                const status = data.episodeStatuses[episode.id] ?? 0;
                const savedForLater = watchLaterEpisodeIds.has(episode.id);
                return (
                  <div className="relative min-w-0" key={episode.id}>
                    <Button
                      className={`episode-grid-button w-full status-${status} ${selected ? 'selected' : ''} ${!episodeSelectionMode ? 'pr-6!' : ''}`.trim()}
                      aria-pressed={episodeSelectionMode ? selected : undefined}
                      title={
                        episodeSelectionMode
                          ? `${episode.name_cn || episode.name || `EP.${episode.sort}`} · ${episodeStatusLabels[status]}`
                          : t('playMediaResource')
                      }
                      onClick={() => {
                        if (episodeSelectionMode) {
                          toggleEpisode(episode.id, !selected);
                        } else {
                          openMediaSearch(episode);
                        }
                      }}
                    >
                      {episode.sort}
                    </Button>
                    {!episodeSelectionMode ? (
                      <Button
                        variant="plain"
                        className={`absolute top-1/2 right-1 z-1 flex size-4.5 -translate-y-1/2 items-center justify-center p-0 ${savedForLater ? 'text-(--input-accent)!' : 'text-(--muted)!'}`}
                        aria-label={t(savedForLater ? 'removeWatchLater' : 'addWatchLater')}
                        title={t(savedForLater ? 'removeWatchLater' : 'addWatchLater')}
                        disabled={watchLaterUpdatingEpisodeIds.has(episode.id)}
                        onClick={() => void toggleWatchLater(episode)}
                      >
                        {savedForLater ? <BookmarkCheck className="size-3!" /> : <Bookmark className="size-3!" />}
                      </Button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <EpisodeList
              episodes={episodes.data}
              episodeStatuses={data.episodeStatuses}
              selectionMode={episodeSelectionMode}
              selectedEpisodeIds={selectedEpisodeIds}
              watchLaterEpisodeIds={watchLaterEpisodeIds}
              watchLaterUpdatingEpisodeIds={watchLaterUpdatingEpisodeIds}
              onToggleSelection={toggleEpisode}
              onToggleWatchLater={(episode) => void toggleWatchLater(episode)}
              onPlay={openMediaSearch}
            />
          )}
        </section>
      )}
      <MediaSourceDialog
        subject={subject}
        episode={mediaEpisode}
        onCancel={closeMediaSearch}
        onPlay={(result, episode) => {
          const search = new URLSearchParams({
            download: result.download.uuid,
            episode: String(episode.ep ?? episode.sort),
            episodeId: String(episode.id),
            subject: String(subject.id),
          });
          navigate(`/playback?${search.toString()}`);
        }}
      />
      {characters.length > 0 && (
        <section className="detail-section">
          <h2>{t('characters')}</h2>
          <div className="character-grid">
            {characters.slice(0, charactersExpanded ? undefined : 16).map((character) => {
              const avatar = safeUrl(character.images?.medium ?? character.images?.small);
              return (
                <a
                  className="character-card min-w-0 text-inherit no-underline"
                  key={character.id}
                  href={`https://bgm.tv/character/${character.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div>
                    {avatar ? <img src={avatar} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <UserRound />}
                  </div>
                  <strong>{character.name}</strong>
                  <span>{character.relation}</span>
                  {character.actors.length > 0 && (
                    <small>{character.actors.map((actor) => actor.name).join(' / ')}</small>
                  )}
                </a>
              );
            })}
          </div>
          {characters.length > 16 && (
            <div className="mt-5.5 flex justify-center">
              <Button className="character-toggle" onClick={() => setCharactersExpanded((value) => !value)}>
                {charactersExpanded ? t('collapse') : t('expandAll')}
                {!charactersExpanded && <small>+{characters.length - 16}</small>}
              </Button>
            </div>
          )}
        </section>
      )}
      {staff.length > 0 && (
        <section className="detail-section">
          <h2>
            {t('staff')}
            <small>{staff.length}</small>
          </h2>
          <div className="person-grid">
            {staff.slice(0, staffExpanded ? undefined : 24).map((person) => {
              const avatar = safeUrl(person.images?.medium ?? person.images?.small);
              return (
                <a
                  className="person-card"
                  key={`${person.id}-${person.relation}`}
                  href={`https://bgm.tv/person/${person.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="person-avatar">
                    {avatar ? <img src={avatar} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <UserRound />}
                  </div>
                  <strong>{person.name}</strong>
                  <span>{person.relation}</span>
                </a>
              );
            })}
          </div>
          {staff.length > 24 && (
            <div className="mt-5.5 flex justify-center">
              <Button className="staff-toggle" onClick={() => setStaffExpanded((value) => !value)}>
                {staffExpanded ? t('collapse') : t('expandAll')}
                {!staffExpanded && <small>+{staff.length - 24}</small>}
              </Button>
            </div>
          )}
        </section>
      )}
      {relatedAnime.length > 0 && (
        <section className="detail-section">
          <h2>
            {t('relatedSubjects')}
            <small>{relatedAnime.length}</small>
          </h2>
          <div className="grid grid-cols-4 gap-3 max-[1050px]:grid-cols-2 max-[700px]:grid-cols-1">
            {relatedAnime.map((item) => {
              const cover = safeUrl(item.images?.large ?? item.images?.common ?? item.images?.medium);
              return (
                <Button
                  variant="plain"
                  className="related-subject-card"
                  key={item.id}
                  onClick={() => navigate(`/subjects/${item.id}`)}
                >
                  <div className="related-subject-cover">
                    {cover ? <img src={cover} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <Image />}
                  </div>
                  <div>
                    <span>{item.relation}</span>
                    <strong>{item.name_cn || item.name}</strong>
                    {item.name_cn && <small>{item.name}</small>}
                  </div>
                </Button>
              );
            })}
          </div>
        </section>
      )}
    </section>
  );
};
